import React, { useEffect } from 'react';
import { useToast } from '../../../../../components/ui/toast/ToastContext';
import BarRoundedChart from '../../../../../components/ui/charts/BarRoundedChart';
import DonutChart from '../../../../../components/ui/charts/DonutChart';
import ProgressDonutChart from '../../../../../components/ui/charts/ProgressDonutChart';
import CurrencyText from '../../../../../components/ui/labels/CurrencyText';
import { ArrowDownCircle, ArrowUpCircle, Check } from 'lucide-react';
import { camelizeKeys } from '../../../../../utils/camelizeKeys';
import Button from '../../../../../components/ui/buttons/Button';
import ClientTransactionModal from '../../../../../components/modals/clientTransaction/ClientTransactionModal';
import CustomButton from '../../../../../components/ui/buttons/CustomButton';
import { formatCurrency } from '../../../../../utils/formatters';

const InvestmentProfileTab = ({ clientId, onSuccess, scope, canWrite }) => {
  const { addToast } = useToast();

  const [data, setData] = React.useState({
    transactions: [],
    totalPatrimony: 0,
    totalPatrimonyBtg: 0,
    totalFixedPatrimony: 0,
    expensesTotal: 0,
    incomeTotal: 0,
    savingsTotal: 0,
    activeIncomeTotal: 0,
    passiveIncomeTotal: 0,
    totalFinancialPatrimony: 0,
  });

  const [isSaving, setIsSaving] = React.useState(false);
  const [showCreateTransactionModal, setShowCreateTransactionModal] = React.useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = React.useState(null);
  const [transactionType, setTransactionType] = React.useState(null);

  useEffect(() => {
    fetchData();
  }, [clientId, scope]);

  const fetchData = () => {
    fetch(`/app/planner/client/${clientId}/dashboard/investment_profile?scope=${scope}`)
      .then((res) => res.json())
      .then((data) => {
        const camelData = camelizeKeys(data);

        setData(camelData);
      });
  };

  const handleModalSuccess = () => {
    setIsSaving(false);
    setShowCreateTransactionModal(false);
    setSelectedTransactionId(null);
    fetchData();
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-6 gap-5">
        {/* 1 */}
        <div className="flex flex-col col-span-2 gap-5">
          <div className="bg-white rounded-custom20 px-6 py-4 flex flex-col gap-2">
            <p className="text-lg text-blueDarker font-semibold">Patrimônio financeiro total</p>
            <CurrencyText size="large" color="text-blueHighlight" value={data.totalFinancialPatrimony} />
          </div>
          <div className="flex gap-5">
            <div className="bg-white rounded-custom20 w-full px-6 py-4 gap-2 flex flex-col">
              <p className="text-lg text-blueDarker font-semibold">Renda</p>
              <CurrencyText size="small" color="text-tealMedium" value={data.incomeTotal} />
            </div>
            <div className="bg-white rounded-custom20 w-full px-6 py-4 gap-2 flex flex-col">
              <p className="text-lg text-blueDarker font-semibold">Patrimônio Imobilizado</p>
              <CurrencyText size="small" color="text-tealMedium" value={data.totalFixedPatrimony} />
            </div>
          </div>
          <div className="flex gap-5">
            <div className="bg-white rounded-custom20 w-full px-6 py-4 gap-2 flex flex-col">
              <div className="flex items-center gap-2">
                <ArrowDownCircle size={20} strokeWidth={1.75} className="text-blueHighlight" />
                <p className="text-lg text-blueDarker font-semibold">Quanto gasta</p>
              </div>
              <CurrencyText size="small" color="text-redSoft" value={data.expensesTotal} />
            </div>
            <div className="bg-white rounded-custom20 w-full px-6 py-4 gap-2 flex flex-col">
              <div className="flex items-center gap-2">
                <ArrowUpCircle size={20} strokeWidth={1.75} className="text-blueHighlight" />
                <p className="text-lg text-blueDarker font-semibold">Quanto poupa</p>
              </div>
              <CurrencyText size="small" color="text-tealMedium" value={data.savingsTotal} />
            </div>
          </div>
          {/* <div className="h-full">
            <div className="bg-white rounded-custom20 w-full px-6 py-4 gap-2 flex flex-col">
              <p className="text-lg text-blueDarker font-semibold">Insights</p>
              <div className="text-base text-blueDarker flex gap-1 items-baseline">
                <Check size={16} strokeWidth={3} className="text-tealMedium" />
                <span>Você poupa X% da sua renda. Parabéns, está acima da média para o seu perfil!</span>
              </div>
              <div className="text-base text-blueDarker flex gap-1 items-baseline">
                <Check size={16} strokeWidth={3} className="text-tealMedium" />
                <span>Você aumentou X% do seu patrimônio esse mês. Y% a mais que no mês anterior.</span>
              </div>
              <div className="text-base text-blueDarker flex gap-1 items-baseline">
                <Check size={16} strokeWidth={3} className="text-tealMedium" />
                <span>
                  Você está R$X acima da meta patrimonial estipulada para você, baseado no seu perfil de risco.
                </span>
              </div>
            </div>
          </div> */}
        </div>

        {/* 2 */}
        <div className="gap-5 flex flex-col col-span-2 h-full">
          <div className="bg-white rounded-custom20 p-6 flex flex-col gap-4 h-full">
            <div className="flex items-center gap-2">
              <ArrowUpCircle size={20} strokeWidth={1.75} className="text-tealMedium" />
              <p className="text-lg text-blueDarker font-semibold">Receitas Ativas</p>
            </div>

            {data.transactions.filter((tx) => tx.transactionType === 'active_income').length > 0 && (
              <div className="overflow-y-auto flex flex-col gap-2">
                {data.transactions
                  .filter((tx) => tx.transactionType === 'active_income')
                  .map((tx) => (
                    <div
                      key={tx.id}
                      className="flex justify-between items-center p-3 cursor-pointer hover:bg-graySoft bg-blueLight rounded-xl text-base"
                      onClick={() => setSelectedTransactionId(tx.id)}
                    >
                      <span className="text-tealMedium font-semibold">{tx.description}</span>
                      <span className="text-blueDarker">{formatCurrency(tx.amount)}</span>
                    </div>
                  ))}
              </div>
            )}

            <div className="mt-auto flex justify-between items-center">
              <div className="flex font-medium items-center gap-2">
                <span className="text-sm text-tealBase">TOTAL: </span>
                <span className="text-xl blueDark">{formatCurrency(data.activeIncomeTotal)}</span>
              </div>
              {canWrite && (
                <CustomButton
                  onClick={() => {
                    setShowCreateTransactionModal(true);
                    setTransactionType('active_income');
                  }}
                  disabled={isSaving}
                >
                  NOVA RECEITA
                </CustomButton>
              )}
            </div>
          </div>

          <div className="bg-white rounded-custom20 p-6 flex flex-col gap-4 h-full">
            <div className="flex items-center gap-2">
              <ArrowUpCircle size={20} strokeWidth={1.75} className="text-tealMedium" />
              <p className="text-lg text-blueDarker font-semibold">Receitas Passivas</p>
            </div>

            {data.transactions.filter((tx) => tx.transactionType === 'passive_income').length > 0 && (
              <div className="overflow-y-auto flex flex-col gap-2">
                {data.transactions
                  .filter((tx) => tx.transactionType === 'passive_income')
                  .map((tx) => (
                    <div
                      key={tx.id}
                      className="flex justify-between items-center p-3 cursor-pointer hover:bg-graySoft bg-blueLight rounded-xl text-base"
                      onClick={() => setSelectedTransactionId(tx.id)}
                    >
                      <span className="text-tealMedium font-semibold">{tx.description}</span>
                      <span className="text-blueDarker">{formatCurrency(tx.amount)}</span>
                    </div>
                  ))}
              </div>
            )}

            <div className="mt-auto flex justify-between items-center">
              <div className="flex font-medium items-center gap-2">
                <span className="text-sm text-tealBase">TOTAL: </span>
                <span className="text-xl blueDark">{formatCurrency(data.passiveIncomeTotal)}</span>
              </div>
              {canWrite && (
                <CustomButton
                  onClick={() => {
                    setShowCreateTransactionModal(true);
                    setTransactionType('passive_income');
                  }}
                  disabled={isSaving}
                >
                  NOVA RECEITA
                </CustomButton>
              )}
            </div>
          </div>
        </div>

        {/* 3 */}
        <div className="bg-white rounded-custom20 p-6 col-span-2 flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <ArrowDownCircle size={20} strokeWidth={1.75} className="text-redSoft" />
            <p className="text-lg text-blueDarker font-semibold">Despesas</p>
          </div>

          {data.transactions.filter((tx) => tx.transactionType === 'expense').length > 0 && (
            <div className="overflow-y-auto flex flex-col gap-2">
              {data.transactions
                .filter((tx) => tx.transactionType === 'expense')
                .map((tx) => (
                  <div
                    key={tx.id}
                    className="flex justify-between items-center p-3 cursor-pointer hover:bg-graySoft bg-redExtraLight2 rounded-xl text-base"
                    onClick={() => setSelectedTransactionId(tx.id)}
                  >
                    <span className="text-redSoft font-semibold">{tx.description}</span>
                    <span className="text-blueDarker">{formatCurrency(tx.amount)}</span>
                  </div>
                ))}
            </div>
          )}

          <div className="mt-auto flex justify-between items-center">
            <div className="flex font-medium items-center gap-2">
              <span className="text-sm text-redSoft">TOTAL: </span>
              <span className="text-xl blueDark">{formatCurrency(data.expensesTotal)}</span>
            </div>
            {canWrite && (
              <CustomButton
                onClick={() => {
                  setShowCreateTransactionModal(true);
                  setTransactionType('expense');
                }}
                disabled={isSaving}
                className="bg-redSoft border-redExtraDark"
              >
                NOVA DESPESA
              </CustomButton>
            )}
          </div>
        </div>
      </div>

      {canWrite && showCreateTransactionModal && (
        <ClientTransactionModal
          clientId={clientId}
          scope={scope}
          onClose={() => setShowCreateTransactionModal(false)}
          onSuccess={handleModalSuccess}
          transactionType={transactionType}
        />
      )}

      {selectedTransactionId && (
        <ClientTransactionModal
          clientId={clientId}
          transactionId={selectedTransactionId}
          scope={scope}
          onClose={() => setSelectedTransactionId(null)}
          onSuccess={handleModalSuccess}
          canWrite={canWrite}
        />
      )}
    </div>
  );
};

export default InvestmentProfileTab;
